import { useEffect, useMemo, useState } from 'react';
import { Icon } from './Icon';
import { eventBus } from '@/shared/EventBus';
import { useGameStore } from '@/ui/store/gameStore';
import { PLAZA_COMMS_PRESETS } from '@/config/plazaCommunication.config';
import { PETS } from '@/config/pets.config';
import { ENABLED_BIOMES } from '@/config/biomes.config';
import { petAssetUrl } from '@/assets/paths';
import { RARITY_ORDER, type PetDef } from '@/domain/types/pet';

type PlazaCommsPanelMode = 'presets' | 'pet-picker';

// Tylko pety z biomów faktycznie uruchomionych (enabled). Biom z `enabled:false`
// (np. Atlantyda przed go-live) nie może wyciekać do pickera „szukam peta".
const enabledBiomeIds = new Set(ENABLED_BIOMES.map((b) => b.id));

const PET_CHOICES = [...PETS]
  .filter((p) => p.origin.biomeId && enabledBiomeIds.has(p.origin.biomeId))
  .sort((a, b) => {
    const rarityDelta = RARITY_ORDER[b.rarity] - RARITY_ORDER[a.rarity];
    if (rarityDelta !== 0) return rarityDelta;
    return a.name.pl.localeCompare(b.name.pl, 'pl');
  });

function normalizedPetQuery(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase('pl-PL')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function petIconSrc(pet: PetDef): string {
  const biomeId = pet.origin.biomeId;
  return biomeId ? petAssetUrl(pet.art.textureKey, biomeId) : '/ui/ui_comm_pet_wanted.webp';
}

/**
 * PlazaCommsBar — presetowe komunikaty nad awatarem w PlazaScene.
 *
 * Świadomie bez inputa tekstowego: dzieci wybierają gotowy preset, serwer
 * waliduje allowlistę i broadcastuje bubble. `TRADE` otwiera konto u gościa,
 * bo handel jest dla gościa zablokowany decyzją D16.
 */
export function PlazaCommsBar(): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState<PlazaCommsPanelMode>('presets');
  const [petQuery, setPetQuery] = useState('');
  const activeScene = useGameStore((s) => s.activeScene);
  const openModal = useGameStore((s) => s.openModal);
  const hatchPhase = useGameStore((s) => s.hatchPhase);
  const isTeleporting = useGameStore((s) => s.isTeleporting);
  const trade = useGameStore((s) => s.trade);
  const currentUser = useGameStore((s) => s.currentUser);
  const setOpenModal = useGameStore((s) => s.setOpenModal);
  const setTradeNotice = useGameStore((s) => s.setTradeNotice);

  const hidden =
    activeScene !== 'plaza' ||
    openModal !== null ||
    hatchPhase !== null ||
    isTeleporting ||
    trade !== null;

  useEffect(() => {
    if (hidden) setExpanded(false);
  }, [hidden]);

  useEffect(() => {
    if (expanded) return;
    setMode('presets');
    setPetQuery('');
  }, [expanded]);

  const filteredPets = useMemo(() => {
    const query = normalizedPetQuery(petQuery);
    if (!query) return PET_CHOICES;
    return PET_CHOICES.filter((pet) => {
      const name = normalizedPetQuery(`${pet.name.pl} ${pet.name.en} ${pet.id}`);
      return name.includes(query);
    });
  }, [petQuery]);

  if (hidden) return null;

  const isGuest = currentUser?.is_guest === true;

  return (
    <div className="absolute bottom-4 left-1/2 z-50 -translate-x-1/2 pointer-events-auto">
      {expanded && (
        <div className="mb-2 w-[min(92vw,400px)] rounded-2xl bg-white/95 p-2 shadow-xl">
          {mode === 'presets' ? (
            <div className="grid grid-cols-2 gap-1.5">
              {PLAZA_COMMS_PRESETS.map((preset) => {
                const locked = preset.requiresFullAccount && isGuest;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => {
                      if (locked) {
                        setTradeNotice('Załóż konto, aby pokazać TRADE.');
                        setOpenModal('account');
                        setExpanded(false);
                        return;
                      }
                      if (preset.id === 'looking_for_pet') {
                        setMode('pet-picker');
                        setPetQuery('');
                        return;
                      }
                      eventBus.emit('plaza:emoteSend', { presetId: preset.id });
                      setExpanded(false);
                    }}
                    className={`min-w-[78px] rounded-xl border border-meadow-200 bg-meadow-50 px-2 py-1.5 text-left shadow-sm transition active:scale-95 ${
                      locked ? 'opacity-70' : 'hover:bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon
                        src={`/ui/${preset.iconKey}.webp`}
                        fallback="?"
                        alt={preset.label}
                        className="h-8 w-8"
                      />
                      <span className="text-xs font-bold text-meadow-800">{preset.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div>
              <div className="mb-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMode('presets')}
                  aria-label="Wróć do komunikatów"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-meadow-200 bg-meadow-50 shadow-sm transition active:scale-95"
                >
                  <Icon src="/ui/ui_arrow_left.webp" fallback="<" alt="wróć" className="h-6 w-6" />
                </button>
                <Icon src="/ui/ui_comm_pet_wanted.webp" fallback="?" alt="" className="h-10 w-10" />
                <span className="text-sm font-extrabold text-meadow-900">Szukam peta</span>
              </div>

              <input
                value={petQuery}
                onChange={(e) => setPetQuery(e.target.value)}
                placeholder="Szukaj peta"
                className="h-9 w-full rounded-xl border border-meadow-200 bg-white px-3 text-sm font-bold text-meadow-900 outline-none focus:border-meadow-400"
              />

              <div className="mt-2 max-h-[64vh] space-y-1 overflow-y-auto pr-1">
                <button
                  type="button"
                  onClick={() => {
                    eventBus.emit('plaza:emoteSend', { presetId: 'looking_for_pet' });
                    setExpanded(false);
                  }}
                  className="flex w-full items-center gap-2 rounded-xl border border-meadow-200 bg-meadow-50 px-2 py-1.5 text-left shadow-sm transition hover:bg-white active:scale-95"
                >
                  <Icon src="/ui/ui_comm_pet_wanted.webp" fallback="?" alt="" className="h-12 w-12" />
                  <span className="min-w-0 flex-1 truncate text-xs font-extrabold text-meadow-900">
                    Dowolny pet
                  </span>
                </button>

                {filteredPets.map((pet) => (
                  <button
                    key={pet.id}
                    type="button"
                    onClick={() => {
                      eventBus.emit('plaza:emoteSend', {
                        presetId: 'looking_for_pet',
                        petDefId: pet.id,
                      });
                      setExpanded(false);
                    }}
                    className="flex w-full items-center gap-2 rounded-xl border border-meadow-200 bg-white px-2 py-1.5 text-left shadow-sm transition hover:bg-meadow-50 active:scale-95"
                  >
                    <Icon src={petIconSrc(pet)} fallback="?" alt="" className="h-16 w-16 shrink-0" />
                    <span className="min-w-0 flex-1 truncate text-xs font-extrabold text-meadow-900">
                      {pet.name.pl}
                    </span>
                    <span className="shrink-0 rounded-full bg-meadow-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-meadow-700">
                      {pet.rarity}
                    </span>
                  </button>
                ))}

                {filteredPets.length === 0 && (
                  <div className="rounded-xl border border-meadow-200 bg-meadow-50 px-3 py-2 text-xs font-bold text-meadow-700">
                    Brak wyników
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? 'Zamknij komunikaty' : 'Otwórz komunikaty'}
        className="flex h-16 min-w-[168px] items-center justify-center gap-2 rounded-full bg-white/95 px-4 shadow-xl transition active:scale-95"
      >
        <Icon src="/ui/ui_btn_comms.webp" fallback="?" alt="komunikaty" className="h-10 w-10" />
        <span className="text-sm font-extrabold text-meadow-900">Komunikaty</span>
      </button>
    </div>
  );
}
